<p-skeleton *ngIf="!loaded; else header" width="100%" height="3.75rem" styleClass="fixed"></p-skeleton>

<ng-template #header>
  <header>
    <nav
      class="navbar theme-background-color fixed z-40 flex w-full items-center justify-between px-4 shadow-sm md:px-8 lg:px-4 xl:px-8"
      aria-label="Main menu">
      <!-- Navbar overlay when editMode is active in dashboard -->
      <div
        *ngIf="editModeEnabled"
        class="p-component-overlay p-drawer-mask p-component-overlay-enter absolute left-0 z-10 h-full w-full"></div>
      <div class="flex items-center">
        <a
          *ngIf="theme?.logo"
          [routerLink]="['/']"
          class="navbar-icon h-2-5rem mr-2 flex max-w-20 cursor-pointer items-center lg:mr-0 xl:mr-2"
          title="home">
          <img [src]="'data:image/png;base64,' + theme?.logo" class="h-auto max-h-full w-full"
        /></a>
        <div
          *ngIf="userHasRoles"
          class="p-buttonset p-buttonset-nav hidden md:flex"
          [ngClass]="{
            'ml-6 lg:ml-1 xl:ml-6': theme?.logo
          }">
          <a
            type="button"
            class="p-button theme-alternating-color theme-border-color theme-text-color"
            [routerLink]="['suiteview', 'objecttypes']"
            [title]="'new.route' | translate">
            {{ 'new.route' | translate }}
          </a>
          <button
            #favouritesToggle
            class="theme-alternating-color theme-border-color theme-text-color"
            pButton
            type="button"
            icon="fa-solid fa-star"
            (click)="toggleFavourites()"
            [title]="'common.favourites' | translate">
            <app-favourites
              [visible]="favouritesVisible"
              [menuItems]="starredMenuItems"
              [button]="favouritesToggle"
              (hide)="toggleFavourites()"></app-favourites>
          </button>
        </div>
        <a
          [routerLink]="['ROUTES.calendar'] | localize"
          class="p-button p-button-navbar-icon mx-6 hidden cursor-pointer items-center no-underline md:flex lg:mx-2 xl:mx-6"
          [title]="'calendar.route' | translate"
          [attr.aria-label]="'calendar.route' | translate"
          >{{ 'calendar.route' | translate }}</a
        >
      </div>
      <!-- Desktop menu items -->
      <div class="hidden h-full items-center lg:flex">
        <i class="fa-regular fa-arrows-rotate theme-text-color fa-spin mr-8 text-xl" *ngIf="isPageLoading()"></i>

        <ng-container [ngTemplateOutlet]="searchBar"></ng-container>

        <p-button
          styleClass="p-button-navbar-icon"
          *ngIf="countMeetingPointers > 0"
          (click)="onShowMeetingPointers()"
          [title]="countMeetingPointers + ' ' + ('current.meetings' | translate)"
          type="button"
          aria-haspopup="dialog">
          <span class="p-overlay-badge">
            <i class="fa-solid fa-calendar-check theme-text-color"></i>
            <p-badge
              *ngIf="countMeetingPointers > 0"
              [value]="countMeetingPointers + ''"
              severity="danger"
              styleClass="theme-alternating-color theme-text-color" />
          </span>
        </p-button>

        <p-button
          styleClass="p-button-navbar-icon"
          *ngIf="countAnnouncements > 0"
          (click)="onShowAnnouncements()"
          [title]="countAnnouncements + ' ' + ('information.announcements.title' | translate)"
          aria-haspopup="dialog">
          <span class="p-overlay-badge">
            <i class="fa-solid fa-bell theme-text-color"></i>
            <p-badge
              *ngIf="countAnnouncements > 0"
              [value]="countAnnouncements + ''"
              severity="danger"
              styleClass="theme-alternating-color theme-text-color" />
          </span>
        </p-button>

        <a
          class="p-button p-button-navbar-icon no-underline"
          *ngIf="printListModule"
          [routerLink]="['suiteview', 'print']"
          [title]="'information.printlist.title' | translate"
          [attr.aria-label]="'information.printlist.title' | translate">
          <span class="p-overlay-badge">
            <i class="fa-solid fa-mailbox theme-text-color"></i>
            <p-badge
              *ngIf="printListModule.urgent || printListModule.counter > 0"
              [value]="printListModule.urgent ? '!' : printListModule.counter + ''"
              severity="danger"
              styleClass="theme-alternating-color theme-text-color" />
          </span>
        </a>

        <a
          class="p-button p-button-navbar-icon no-underline"
          *ngIf="calculationModule && calculationModule.url"
          [routerLink]="['suiteview', 'calculation-module']"
          [title]="'information.calculation.title' | translate"
          [attr.aria-label]="'information.calculation.title' | translate">
          <i class="fa-solid fa-calculator theme-text-color"></i>
        </a>

        <a
          class="p-button p-button-navbar-icon overflow-visible no-underline"
          [routerLink]="['ROUTES.userdata', 'ROUTES.generatedfiles'] | localize"
          [title]="exportsLength + ' ' + ('userdata.generatedfiles.title' | translate)"
          [attr.aria-label]="exportsLength + ' ' + ('userdata.generatedfiles.title' | translate)">
          <span class="p-overlay-badge">
            <i class="fa-solid fa-arrow-down-to-line theme-text-color"></i>
            <p-badge
              *ngIf="exportsLength > 0"
              [value]="exportsLength + ''"
              severity="danger"
              styleClass="theme-alternating-color theme-text-color" />
          </span>
        </a>

        <p-button
          *ngIf="voting$ | async"
          styleClass="p-button-navbar-icon"
          (click)="showVotingDialog()"
          [title]="'voting.vote.title' | translate"
          aria-haspopup="dialog">
          <i class="fa-solid fa-check-to-slot theme-text-color"></i>
        </p-button>

        <app-tiered-menu-button
          *ngIf="helpMenuItems && helpMenuItems.length > 0"
          [label]="'information.help.title' | translate"
          iconRight="fa-solid fa-chevron-down"
          textClass="theme-text-color"
          buttonClass="p-button-navbar"
          [menuItems]="helpMenuItems"></app-tiered-menu-button>

        <app-tiered-menu-button
          *ngIf="userMenuItems && userMenuItems.length > 0"
          [label]="userGivenName"
          buttonClass="p-button-navbar whitespace-nowrap"
          textClass="theme-text-color"
          [iconLeft]="userIcon"
          iconRight="fa-solid fa-chevron-down"
          [menuItems]="userMenuItems"></app-tiered-menu-button>
        <app-tiered-menu-button
          *ngIf="configurationMenuItems && configurationMenuItems.length > 0"
          buttonClass="p-button-navbar"
          textClass="theme-text-color"
          iconLeft="fa-solid fa-ellipsis-vertical"
          [menuItems]="configurationMenuItems"
          [title]="'information.configuration.title' | translate">
        </app-tiered-menu-button>
      </div>

      <!-- Open mobile menu handle -->
      <div class="relative flex items-center lg:hidden">
        <div class="p-buttonset p-buttonset-nav button-new-starred" *ngIf="userHasRoles">
          <button
            pButton
            class="p-button p-button-icon-only theme-alternating-color theme-border-color theme-text-color md:hidden"
            icon="fa-regular fa-plus"
            [routerLink]="['suiteview', 'objecttypes']"
            [title]="'new.route' | translate"></button>
        </div>

        <a
          pButton
          class="p-button p-button-navbar-icon theme-text-color no-underline md:hidden"
          icon="fa-regular fa-calendar"
          [routerLink]="['ROUTES.calendar'] | localize"
          [title]="'calendar.route' | translate"></a>

        <ng-container [ngTemplateOutlet]="searchBar"></ng-container>
        <button
          type="button"
          pButton
          icon="fa-solid fa-bars"
          class="p-button-navbar-icon theme-text-color"
          [title]="'common.menu' | translate"
          (click)="toggleMenu()"></button>
      </div>
    </nav>
    <p-drawer
      [(visible)]="menuOpen"
      [showCloseIcon]="false"
      (visibleChange)="visibleChange.emit($event)"
      position="right"
      [autoZIndex]="true"
      [baseZIndex]="2"
      [dismissible]="true"
      styleClass="lg:hidden navbar-sidebar w-full sm:w-96">
      <ng-template pTemplate="header">
        <div class="theme-background-color flex w-full items-center justify-between">
          <h2 class="m-0 text-white">Menu</h2>
          <button
            type="button"
            pButton
            icon="fa-solid fa-xmark"
            class="p-button-navbar-icon p-button-icon-only p-button-round p-0"
            (click)="toggleMenu()"
            [title]="'common.button.close' | translate"></button>
        </div>
      </ng-template>
      <ng-template pTemplate="content">
        <p-panelMenu [model]="mobileMenuItems" [style]="{ width: 'w-full' }"></p-panelMenu>
      </ng-template>
    </p-drawer>
  </header>
</ng-template>

<ng-template #searchBar>
  <div class="relative h-full">
    <app-navbar-live-search [inNavbar]="true" (triggerSearch)="triggerSearch.emit($event)"></app-navbar-live-search>
  </div>
</ng-template>
